<script setup>
import { ref, computed, inject } from 'vue'
import { NSlider, NIconWrapper, NConfigProvider } from 'naive-ui'

const props = defineProps({
  emoji: {
    type: String,
    default: '',
  },
  disabled: {
    type: Boolean,
    default: false,
  },
  modelValue: {
    type: Number,
    default: 50,
  },
  startLabel: {
    type: String,
    default: '',
  },
  stopLabel: {
    type: String,
    default: '',
  },
})

const emit = defineEmits(['update:modelValue', 'release'])
const pollParams = inject('pollParams')

const sliderValue = ref(props.modelValue)
const isDragging = ref(false)

const thumbImage = computed(() => {
  if (props.emoji) return ''

  const stationLogos = {
    'joe-theme': '/joe.svg',
    'willy-theme': '/willy.svg',
    'qmusic-theme': '/q-music-logo-white.svg',
  }

  return stationLogos[pollParams.theme] || ''
})

const themeOverrides = computed(() => {
  return {
    common: {
      primaryColor: pollParams.primaryColor,
    },
  }
})

const handleInput = (value) => {
  sliderValue.value = value
  emit('update:modelValue', value)
}

const handleDragStart = () => {
  isDragging.value = true
}

const handleDragEnd = () => {
  isDragging.value = false
  emit('release', sliderValue.value)
}

const sliderClass = computed(() => ({
  'emoji-slider': true,
  dragging: isDragging.value,
  disabled: props.disabled,
}))
</script>

<template>
  <div :class="sliderClass">
    <n-config-provider :theme-overrides="themeOverrides">
      <div class="emoji-slider__track">
        <n-slider
          v-model:value="sliderValue"
          :min="0"
          :max="100"
          :disabled="disabled"
          :tooltip="false"
          :show-tooltip="false"
          @update:value="handleInput"
          @dragstart="handleDragStart"
          @dragend="handleDragEnd"
        >
          <template #thumb>
            <n-icon-wrapper :size="60" :border-radius="30">
              <img v-if="thumbImage" :src="thumbImage" class="emoji-slider__image" alt="Slider thumb" />
              <div v-else-if="emoji" class="emoji-slider__emoji">{{ emoji }}</div>
            </n-icon-wrapper>
          </template>
        </n-slider>
      </div>
    </n-config-provider>
    <div v-if="startLabel || stopLabel" class="emoji-slider__labels">
      <span class="emoji-slider__label">{{ startLabel }}</span>
      <span class="emoji-slider__label">{{ stopLabel }}</span>
    </div>
  </div>
</template>

<style scoped>
.emoji-slider {
  width: 100%;
  padding: 2rem 0;
  -webkit-tap-highlight-color: transparent;
  user-select: none;
  -webkit-user-select: none;
  container-type: inline-size;
}

.emoji-slider__track {
  position: relative;
  padding: 1.5rem 0 0.25rem 0;
}

.emoji-slider__emoji {
  font-size: 2.5rem;
  display: block;
  filter: drop-shadow(0 2px 4px rgba(0, 0, 0, 0.2));
  transition: transform 0.1s ease;
}

.emoji-slider__image {
  width: 2.5rem;
  height: 2.5rem;
  display: block;
  object-fit: contain;
  filter: drop-shadow(0 2px 4px rgba(0, 0, 0, 0.2));
  transition: transform 0.1s ease;
}

.dragging .emoji-slider__emoji,
.dragging .emoji-slider__image {
  transform: scale(1.1);
}

.emoji-slider__labels {
  display: flex;
  justify-content: space-between;
  margin-top: 0.5rem;
  padding: 0 0.5rem;
}

.emoji-slider__label {
  font-size: clamp(1rem, 6cqi, 3rem);
  font-weight: 600;
  color: #666;
}
</style>
